import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { Grid, Card, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import CircularLoading from "../components/CircularLoading";
import UsersCombo from "../components/UsersCombo";
import AlertDialog from "../components/AlertDialog";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { colors } from '../components/Theme/WebTheme';
import { MAIN_COLOR, SECONDORY_COLOR, FONT_FAMILY } from "../common/sharedFunctions";
import { createWalletAdjustmentRequest } from '../common/walletAdjustmentRequest';

const money = (value) => `${Number(value || 0).toLocaleString('tr-TR')} TL`;

const WalletAdjust = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const dispatch = useDispatch();
  const settings = useSelector((state) => state.settingsdata.settings);
  const usersdata = useSelector((state) => state.usersdata);
  const actorUid = useSelector((state) => state.auth.profile?.uid);
  const { fetchUsersOnce, adjustWalletAsAdmin } = api;

  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [operation, setOperation] = useState('credit');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: "" });

  const requestRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [requestError, setRequestError] = useState('');
  useEffect(() => {
    requestRef.current = null;
    setPending(null);
    setRequestError('');
    setSelected(null); setAmount(''); setReason('');
    setConfirmOpen(false); setLoading(false);
    if (!actorUid) return;
    try {
      requestRef.current = createWalletAdjustmentRequest(window.sessionStorage, actorUid, undefined, Number.isInteger(settings?.decimal) ? settings.decimal : 2);
      setPending(requestRef.current.pending());
    } catch (error) {
      setRequestError(error.message);
    }
    return () => { requestRef.current = null; };
  }, [actorUid, settings?.decimal]);

  useEffect(() => { dispatch(fetchUsersOnce()); }, [dispatch, fetchUsersOnce]);

  // UsersCombo labels each option by `desc`, so the raw user records have to be mapped
  // before they reach it — without this the dropdown renders "No options" even though the
  // list is full. Admins are excluded: their wallet is not an operational balance.
  useEffect(() => {
    const source = usersdata.staticusers || usersdata.users;
    if (!source) { setUsers([]); return; }
    setUsers(source
      .filter((user) => user.usertype !== 'admin')
      .map((user) => ({
        firstName: user.firstName,
        lastName: user.lastName,
        mobile: user.mobile,
        email: user.email,
        uid: user.uid || user.id,
        usertype: user.usertype,
        walletBalance: user.walletBalance,
        // Many records have no name at all, and a row showing only a phone number is hard
        // to pick out of ~7,800 entries. Fall back to whatever identifies the person.
        desc: [
          [user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || user.mobile || 'İsimsiz kullanıcı',
          user.mobile ? `(${user.mobile})` : '',
          user.email && [user.firstName, user.lastName].filter(Boolean).length ? user.email : '',
        ].filter(Boolean).join(' '),
      })));
  }, [usersdata.staticusers, usersdata.users]);

  const parsedAmount = Number(amount);
  const valid = selected && Number.isFinite(parsedAmount) && parsedAmount > 0 && reason.trim().length > 2 && reason.trim().length <= 240;
  const balance = selected ? Number(selected.walletBalance || 0) : 0;
  const projected = operation === 'credit' ? balance + parsedAmount : balance - parsedAmount;

  const submit = async () => {
    setConfirmOpen(false);
    if (!settings?.AllowCriticalEditsAdmin) {
      setCommonAlert({ open: true, msg: t('demo_mode') });
      return;
    }
    const request = requestRef.current;
    if (!request || request.isBusy() || (!request.pending() && !valid)) return;
    setLoading(true);
    try {
      const result = await request.execute({
        uid: selected?.uid || selected?.id, amount: parsedAmount, operation, reason: reason.trim(),
      }, payload => dispatch(adjustWalletAsAdmin({ ...payload, expectedActorUid: actorUid })));
      if (requestRef.current !== request) return;
      setCommonAlert({ open: true, msg: t('wallet_adjust_done', { balance: money(result.walletBalance) }) });
      setAmount(''); setReason('');
      try { await dispatch(fetchUsersOnce()); }
      catch (_) { if (requestRef.current === request) setCommonAlert({ open: true, msg: `İşlem tamamlandı. Yeni bakiye: ${money(result.walletBalance)}. Kullanıcı listesi yenilenemedi; sayfayı yenileyin.` }); }
    } catch (error) {
      if (requestRef.current !== request) return;
      setCommonAlert({ open: true, msg: `${error.message || t('update_failed')}${request.pending() ? ' İşlem bilgileri korundu; tekrar denemede aynı işlem kimliği kullanılacak.' : ' Bilgileri düzelterek yeniden deneyin.'}` });
    } finally {
      if (requestRef.current === request) {
        setPending(request.pending());
        setLoading(false);
      }
    }
  };

  if (loading) return <CircularLoading />;

  return (
    <div style={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <Card style={{ borderRadius: 19, padding: 24, boxShadow: `0px 2px 5px ${SECONDORY_COLOR}` }}>
        <Typography variant="h5" style={{ fontWeight: 'bold', color: MAIN_COLOR, fontFamily: FONT_FAMILY, marginBottom: 4 }}>
          {t('wallet_adjust_title')}
        </Typography>
        <Typography style={{ color: colors.HEADER, fontSize: 13, marginBottom: 20, fontFamily: FONT_FAMILY }}>
          {t('wallet_adjust_hint')}
        </Typography>

        {requestError && <Typography role="alert" color="error">{requestError}</Typography>}
        {pending ? (
          <div>
            <Typography role="alert">Son işlemin sonucu henüz doğrulanmadı. Çift bakiye hareketini önlemek için aynı işlem tekrar kontrol edilecektir.</Typography>
            <Typography>Kullanıcı: {pending.uid} · {pending.operation === 'credit' ? 'Ekleme' : 'Düşüm'}: {money(pending.amount)}</Typography>
            <Typography>Açıklama: {pending.reason}</Typography>
            <Button variant="contained" onClick={submit} disabled={!actorUid || !!requestError} style={{ marginTop: 16 }}>Aynı işlemi tekrar dene</Button>
          </div>
        ) : <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <UsersCombo
              className={null}
              placeholder={t('select_user')}
              users={users}
              value={selected}
              onChange={(event, newValue) => setSelected(newValue)}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            {selected ? (
              <Card style={{ padding: 14, backgroundColor: '#FAFAFA', boxShadow: 'none', border: `1px solid ${colors.TABLE_BORDER}` }}>
                <Typography style={{ fontSize: 13, color: colors.HEADER }}>{t('wallet_current_balance')}</Typography>
                <Typography style={{ fontSize: 24, fontWeight: 'bold', color: MAIN_COLOR }}>{money(balance)}</Typography>
                <Typography style={{ fontSize: 12, color: colors.HEADER }}>
                  {selected.email || selected.mobile || ''}
                </Typography>
              </Card>
            ) : null}
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="op">{t('wallet_operation')}</InputLabel>
              <Select labelId="op" value={operation} label={t('wallet_operation')} onChange={(e) => setOperation(e.target.value)}>
                <MenuItem value="credit">{t('wallet_operation_credit')}</MenuItem>
                <MenuItem value="debit">{t('wallet_operation_debit')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label={t('amount')} value={amount} fullWidth type="number"
              onChange={(e) => setAmount(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            {selected && valid ? (
              <Typography style={{ marginTop: 16, fontSize: 14 }}>
                {t('wallet_projected')}: <b style={{ color: projected < 0 ? '#C62828' : MAIN_COLOR }}>{money(projected)}</b>
              </Typography>
            ) : null}
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('wallet_reason')} value={reason} fullWidth multiline rows={2}
              inputProps={{ maxLength: 240 }}
              onChange={(e) => setReason(e.target.value)}
              helperText={t('wallet_reason_help')}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained" disabled={!valid || !actorUid || !!requestError}
              style={{ backgroundColor: valid ? MAIN_COLOR : colors.TABLE_BORDER, color: colors.WHITE, borderRadius: 19, minHeight: 46, minWidth: 220 }}
              onClick={() => setConfirmOpen(true)}
            >
              {t('wallet_adjust_apply')}
            </Button>
          </Grid>
        </Grid>}
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('wallet_adjust_confirm_title')}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ whiteSpace: 'pre-line' }}>
            {selected ? t('wallet_adjust_confirm', {
              name: [selected.firstName, selected.lastName].filter(Boolean).join(' '),
              operation: operation === 'credit' ? t('wallet_operation_credit') : t('wallet_operation_debit'),
              amount: money(parsedAmount),
              before: money(balance),
              after: money(projected),
            }) : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('cancel')}</Button>
          <Button onClick={submit} style={{ color: MAIN_COLOR, fontWeight: 'bold' }}>{t('wallet_adjust_apply')}</Button>
        </DialogActions>
      </Dialog>

      <AlertDialog open={commonAlert.open} onClose={() => setCommonAlert({ open: false, msg: "" })}>
        {commonAlert.msg}
      </AlertDialog>
    </div>
  );
};

export default WalletAdjust;
